import { useMutation, useQuery } from '@tanstack/react-query';

import {
  BankType,
  CheckAccountHolderRequest,
  ExecuteWiringRequest,
  WiringConnectingStatusRequest,
  checkAccountHolder,
  checkWiringExecution,
  executeWiring,
  getRencentWiring,
  getWiringAccountList,
  getWiringConnectingStatus,
  getSearchReceiverAccounts,
  getWiringResult,
  ReserveLabelingRequest,
  reserveLabeling,
  getStatusCount,
  WIRING_RESULT_STATUS,
  getWiringResultList,
  retryExistWiring,
  cancelWiring,
  deleteWiring,
  deleteWiringList,
} from '~apis/wiring';
import { queryClient } from '~libs/queryClient';

export const WiringQueryKey = {
  default: () => ['wiring'],
  connectingStatus: ({ companyId, wiringBank }: { companyId: string; wiringBank: BankType }) => [
    ...WiringQueryKey.default(),
    'wiringConnectingStatus',
    companyId,
    wiringBank,
  ],
  accountList: ({ companyId, includeBalance }: { companyId: string; includeBalance?: boolean }) => [
    ...WiringQueryKey.default(),
    'wiringAccountList',
    companyId,
    includeBalance,
  ],
  recentList: ({ companyId, keyword }: { companyId: string; keyword?: string }) => [
    ...WiringQueryKey.default(),
    'wiringRecentList',
    companyId,
    keyword,
  ],
  searchRecieverAccounts: ({ companyId, keyword }: { companyId: string; keyword: string }) => [
    ...WiringQueryKey.default(),
    'wiringSearchRecieverAccounts',
    companyId,
    keyword,
  ],
  result: ({ companyId, wiringId }: { companyId: string; wiringId: number }) => [
    ...WiringQueryKey.default(),
    'wiringResult',
    companyId,
    wiringId,
  ],
  statusCount: ({ companyId, from, to }: { companyId: string; from: string; to: string }) => [
    ...WiringQueryKey.default(),
    'wiringStatusCount',
    companyId,
    from,
    to,
  ],
  resultList: ({
    companyId,
    from,
    to,
    userId,
    status,
    accountId,
  }: {
    companyId: string;
    from: string;
    to: string;
    userId?: string;
    status?: WIRING_RESULT_STATUS;
    accountId?: number;
  }) => [
    ...WiringQueryKey.default(),
    'wiringResultList',
    companyId,
    from,
    to,
    userId,
    status,
    accountId,
  ],
};

export function useGetWiringConnectingStatus({
  companyId,
  wiringBank,
}: WiringConnectingStatusRequest) {
  return useQuery({
    queryKey: WiringQueryKey.connectingStatus({ companyId, wiringBank }),
    queryFn: () => getWiringConnectingStatus({ companyId, wiringBank }),
    cacheTime: 0,
  });
}

export function useGetWiringAccountList({
  companyId,
  includeBalance,
}: {
  companyId: string;
  includeBalance?: boolean;
}) {
  return useQuery({
    queryKey: WiringQueryKey.accountList({ companyId, includeBalance }),
    queryFn: () => getWiringAccountList({ companyId, includeBalance }),
    enabled: !!companyId,
  });
}

export function useCheckAccountHolder() {
  return useMutation({
    mutationFn: ({
      companyId,
      request,
    }: {
      companyId: string;
      request: CheckAccountHolderRequest;
    }) =>
      checkAccountHolder({
        companyId,
        request,
      }),
  });
}

export function useExecuteWiring() {
  return useMutation({
    mutationFn: ({ companyId, request }: { companyId: string; request: ExecuteWiringRequest }) =>
      executeWiring({
        companyId,
        request,
      }),
  });
}

export function useRetryWiring() {
  return useMutation({
    mutationFn: ({ companyId, wiringId }: { companyId: string; wiringId: number }) =>
      retryExistWiring({
        companyId,
        wiringId,
      }),
  });
}

export function useWiringExecutionCheck() {
  return useMutation({
    mutationFn: ({ companyId, wiringId }: { companyId: string; wiringId: number }) =>
      checkWiringExecution({ companyId, wiringId }),
  });
}

export function useGetRecentWiringList({
  companyId,
  keyword,
}: {
  companyId: string;
  keyword?: string;
}) {
  return useQuery({
    queryKey: WiringQueryKey.recentList({ companyId, keyword }),
    queryFn: () => getRencentWiring({ companyId, keyword }),
    enabled: !!companyId,
  });
}

export function useGetWiringResult({
  companyId,
  wiringId,
  stopPolling,
}: {
  companyId: string;
  wiringId: number;
  stopPolling?: boolean;
}) {
  return useQuery({
    queryKey: WiringQueryKey.result({ companyId, wiringId }),
    queryFn: () => getWiringResult({ companyId, wiringId }),
    refetchOnMount: true,
    refetchInterval() {
      if (stopPolling) {
        return false;
      }
      return 5000;
    },
  });
}

export function useGetSearchReceiverAccounts({
  companyId,
  keyword,
}: {
  companyId: string;
  keyword: string;
}) {
  return useQuery({
    queryKey: WiringQueryKey.searchRecieverAccounts({ companyId, keyword }),
    queryFn: () => getSearchReceiverAccounts({ companyId, keyword }),
    enabled: !!companyId,
  });
}

export function useGetStatusCount({
  companyId,
  from,
  to,
}: {
  companyId: string;
  from: string;
  to: string;
}) {
  return useQuery({
    queryKey: WiringQueryKey.statusCount({ companyId, from, to }),
    queryFn: () =>
      getStatusCount({
        companyId,
        from,
        to,
      }),
    enabled: !!companyId,
  });
}

export function useGetWiringResultList({
  companyId,
  from,
  to,
  userId,
  status,
  accountId,
}: {
  companyId: string;
  from: string;
  to: string;
  userId?: string;
  status?: WIRING_RESULT_STATUS;
  accountId?: number;
}) {
  return useQuery({
    queryKey: WiringQueryKey.resultList({ companyId, from, to, userId, status, accountId }),
    queryFn: () =>
      getWiringResultList({
        companyId,
        from,
        to,
        userId,
        status,
        accountId,
      }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export function useReserveLabeling() {
  return useMutation({
    mutationFn: ({ companyId, request }: { companyId: string; request: ReserveLabelingRequest }) =>
      reserveLabeling({
        companyId,
        request,
      }),
  });
}

export function useCancelWiring() {
  return useMutation({
    mutationFn: ({ companyId, wiringId }: { companyId: string; wiringId: number }) =>
      cancelWiring({
        companyId,
        wiringId,
      }),
  });
}

export function useDeleteWiring() {
  return useMutation({
    mutationFn: ({ companyId, wiringId }: { companyId: string; wiringId: number }) =>
      deleteWiring({
        companyId,
        wiringId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: WiringQueryKey.default(),
      });
    },
  });
}

export function useDeleteWiringList() {
  return useMutation({
    mutationFn: ({ companyId, wiringIds }: { companyId: string; wiringIds: number[] }) =>
      deleteWiringList({ companyId, wiringIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: WiringQueryKey.default(),
      });
    },
  });
}
